import React from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff'

import type { HolidayImage } from '@/src/types/holidays'
import { Link } from '@/src/i18n/navigation'

import HolidayImageView from '../holidayImage'
import { HOLIDAYS_RADIUS } from '@/src/utils/holidays'

interface PackageCardProps {
    href: string
    image?: HolidayImage
    badge?: string
    /** Shown in place of the offer badge, and dims the artwork. */
    soldOutLabel?: string
    airlineName?: string
    destinationName: string
    /** Pre-formatted, e.g. '6 Days'. */
    durationLabel: string
    tourPackageLabel: string
    tagline?: string
    tags: string[]
    /** Pre-formatted, e.g. 'Group Trip: 1 - 6 December 2026'. */
    tripWindowLabel?: string
    startingFromLabel: string
    /** Pre-formatted, e.g. 'AED 12,599'. */
    priceLabel: string
    viewDetailsLabel: string
    onSelect: () => void
    isMobile: boolean
}

/** Shared look for the two chips that sit on the card artwork. */
const overlayChipSx = {
    height: 26,
    fontSize: '0.688rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
}

const PackageCard: React.FC<PackageCardProps> = ({
    href,
    image,
    badge,
    soldOutLabel,
    airlineName,
    destinationName,
    durationLabel,
    tourPackageLabel,
    tagline,
    tags,
    tripWindowLabel,
    startingFromLabel,
    priceLabel,
    viewDetailsLabel,
    onSelect,
    isMobile
}) => {
    return (
        <Box
            component={Link}
            href={href}
            onClick={onSelect}
            aria-label={`${destinationName} — ${viewDetailsLabel}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                textDecoration: 'none',
                borderRadius: HOLIDAYS_RADIUS.SURFACE,
                overflow: 'hidden',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'borders.default',
                transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
                '&:hover': {
                    boxShadow: 4,
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)'
                }
            }}
        >
            {/* Media */}
            {/*
             * The CMS card art is designed, with the campaign branding across the top, so the box
             * takes the image's own aspect ratio instead of a fixed height — a fixed height plus
             * `cover` cropped roughly half the picture away. The ratio follows the same `isMobile`
             * signal that picks the crop, so the box always describes the file actually loaded:
             * 1500x900 for the mobile crop, 792x720 for the desktop one.
             */}
            <Box
                sx={{
                    position: 'relative',
                    aspectRatio: isMobile ? '1500 / 900' : '792 / 720',
                    bgcolor: 'neutral.100'
                }}
            >
                {image && (
                    // A full departure still links through — people ask about the next one — but
                    // the artwork stops selling it.
                    <Box sx={{ height: '100%', ...(soldOutLabel && { filter: 'grayscale(1)', opacity: 0.55 }) }}>
                        <HolidayImageView image={image} isMobile={isMobile} />
                    </Box>
                )}

                {/*
                 * Offer badge and airline sit on the artwork, one to each top corner, as on the
                 * reference. The airline used to be 10px grey type in the card body, where it read
                 * as absent.
                 *
                 * One flex row rather than two absolutely-placed chips: with fixed percentage caps
                 * a long carrier name ("South African Airways") truncated even when the badge
                 * beside it was short. Sharing a row lets each chip take the width it needs and
                 * only shrink when they genuinely compete.
                 */}
                {(soldOutLabel || badge || airlineName) && (
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            position: 'absolute',
                            top: 12,
                            insetInlineStart: 12,
                            insetInlineEnd: 12,
                            alignItems: 'flex-start',
                            justifyContent: soldOutLabel || badge ? 'space-between' : 'flex-end'
                        }}
                    >
                        {soldOutLabel ? (
                            <Chip
                                label={soldOutLabel}
                                size="small"
                                sx={{
                                    ...overlayChipSx,
                                    flexShrink: 0,
                                    bgcolor: 'error.main',
                                    color: 'common.white'
                                }}
                            />
                        ) : (
                            badge && (
                                <Chip
                                    label={badge}
                                    size="small"
                                    sx={{
                                        ...overlayChipSx,
                                        flexShrink: 1,
                                        bgcolor: 'success.light',
                                        color: 'success.main'
                                    }}
                                />
                            )
                        )}

                        {airlineName && (
                            <Chip
                                icon={<FlightTakeoffIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
                                label={airlineName}
                                size="small"
                                sx={{
                                    ...overlayChipSx,
                                    flexShrink: 0,
                                    bgcolor: 'secondary.main',
                                    color: 'neutral.black'
                                }}
                            />
                        )}
                    </Stack>
                )}

                <Chip
                    label={durationLabel}
                    size="small"
                    sx={{
                        position: 'absolute',
                        bottom: 12,
                        insetInlineStart: 12,
                        height: 22,
                        fontSize: '0.688rem',
                        fontWeight: 700,
                        bgcolor: 'neutral.white',
                        color: 'textColors.primary'
                    }}
                />
            </Box>

            {/* Body */}
            <Stack spacing={1.25} sx={{ p: 2, flex: 1 }}>
                <Box>
                    <Typography
                        component="h3"
                        sx={{
                            fontSize: isMobile ? '1.125rem' : '1.25rem',
                            fontWeight: 700,
                            lineHeight: 1.25,
                            color: 'textColors.primary'
                        }}
                    >
                        {destinationName}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '0.688rem',
                            fontWeight: 600,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: 'textColors.subheading',
                            mt: 0.25
                        }}
                    >
                        {tourPackageLabel}
                    </Typography>
                </Box>

                {tagline && (
                    <Typography
                        sx={{
                            fontSize: '1rem',
                            lineHeight: 1.55,
                            color: 'textColors.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}
                    >
                        {tagline}
                    </Typography>
                )}

                {tags.length > 0 && (
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {tags.map(tag => (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                    height: 24,
                                    fontSize: '0.688rem',
                                    bgcolor: 'neutral.100',
                                    color: 'textColors.subtitle',
                                    border: '1px solid',
                                    borderColor: 'borders.default'
                                }}
                            />
                        ))}
                    </Stack>
                )}

                {tripWindowLabel && (
                    <Typography
                        sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: 'textColors.subtitle',
                            mt: 'auto'
                        }}
                    >
                        {tripWindowLabel}
                    </Typography>
                )}

                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        pt: 1.25,
                        borderTop: '1px solid',
                        borderColor: 'borders.default'
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: '0.688rem', color: 'textColors.subheading' }}>
                            {startingFromLabel}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: isMobile ? '1.125rem' : '1.25rem',
                                fontWeight: 700,
                                color: 'textColors.primary',
                                lineHeight: 1.2
                            }}
                        >
                            {priceLabel}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'primary.main',
                            color: 'neutral.white',
                            flexShrink: 0
                        }}
                    >
                        <ArrowForwardIcon sx={{ fontSize: 18, '[dir=rtl] &': { transform: 'scaleX(-1)' } }} />
                    </Box>
                </Stack>
            </Stack>
        </Box>
    )
}

export { PackageCard }
export type { PackageCardProps }
export default PackageCard
